import React, { useState, useEffect } from "react";
import { useContacts } from "@/hooks/useContacts";
import { getDisplayName, formatWhatsAppJid } from "@/utils/contactUtils";
import * as Icons from "@/components/ui/shared/Icons";
import clsx from "clsx";
import type { Database } from "@/lib/supabase";

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface ContactSearchProps {
  onContactSelect?: (contact: AccountContact) => void;
  onSearchChange?: (searchTerm: string) => void;
  placeholder?: string;
  styles?: string;
}

const ContactSearch: React.FC<ContactSearchProps> = ({ 
  onContactSelect, 
  onSearchChange,
  placeholder = "Pesquisar contatos...",
  styles = "w-[170px] sm:w-[220px] md:w-[270px] xl:w-[417px] min-h-[40px]"
}) => {
  const { contacts } = useContacts();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredContacts, setFilteredContacts] = useState<AccountContact[]>([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredContacts([]);
      setShowResults(false);
      return;
    }

    const filtered = contacts.filter(contact => {
      const displayName = getDisplayName(contact).toLowerCase();
      const phone = formatWhatsAppJid(contact.jid).toLowerCase();
      const search = searchTerm.toLowerCase();
      
      return displayName.includes(search) || phone.includes(search) || contact.jid.includes(search);
    });

    console.log('🔍 [ContactSearch] Filtrando contatos:', {
      searchTerm,
      total_contacts: contacts.length,
      filtered_count: filtered.length,
      filtered_preview: filtered.slice(0, 3).map(c => ({
        id: c.id,
        display_name: getDisplayName(c),
        jid: c.jid
      }))
    });

    setFilteredContacts(filtered.slice(0, 10)); // Limitar a 10 resultados
    setShowResults(true);
  }, [searchTerm, contacts]);

  const handleContactClick = (contact: AccountContact) => {
    console.log('👆 [ContactSearch] Contato selecionado:', contact);
    onContactSelect?.(contact);
    setSearchTerm(getDisplayName(contact));
    setShowResults(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    onSearchChange?.(e.target.value);
  };

  const handleInputFocus = () => {
    if (searchTerm.trim() && filteredContacts.length > 0) {
      setShowResults(true);
    }
  };

  const handleInputBlur = () => {
    // Delay para permitir clique nos resultados
    setTimeout(() => setShowResults(false), 200);
  };

  return (
    <div className="relative">
      <div
        className={clsx(
          "gap-2 rounded-[6px] border border-neutral-500 bg-neutral-600 px-3 flex justify-start items-center",
          styles
        )}
      >
        <Icons.search className="w-[16px] h-[16px]" />
        <input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="grow-1 text-[16px] font-normal text-neutral-50 placeholder:text-neutral-400 w-full h-full border-none focus:outline-none bg-transparent"
        />
      </div>

      {/* Resultados da busca */}
      {showResults && filteredContacts.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-neutral-600 border border-neutral-500 rounded-[6px] shadow-lg z-50 max-h-[300px] overflow-y-auto">
          {filteredContacts.map((contact) => {
            const displayName = getDisplayName(contact);
            const formattedPhone = formatWhatsAppJid(contact.jid);

            return (
              <div
                key={contact.id}
                onClick={() => handleContactClick(contact)}
                className="flex items-center gap-3 p-3 hover:bg-neutral-700 cursor-pointer transition-colors border-b border-neutral-500 last:border-b-0"
              >
                <div className="w-[32px] h-[32px] bg-neutral-400 rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium text-neutral-800">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex flex-col flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-neutral-50 truncate">{displayName}</p>
                  <p className="text-[12px] text-neutral-400 truncate">{formattedPhone}</p>
                </div>
                {contact.is_group && (
                  <span className="text-xs bg-neutral-500 text-neutral-200 px-2 py-1 rounded">
                    Grupo
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ContactSearch;